/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AnimatedGradientBorder } from '../plugin/ui/AnimatedBorder';
import { NoBodyExecutionLoading } from '../plugin/ui/NoBodyExecutionLoading';

import type { AgentManagedBlockLifecycleState } from './agentManagedExtensionPluginType';
import { isExecutingLifecycleState } from './lifecycle';
import { messages } from './messages';
import { NoBodyExecutionError } from './NoBodyExecutionError';

const styles = cssMap({
	wrapper: {
		position: 'relative',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.small'),
		outlineWidth: token('border.width'),
		outlineStyle: 'solid',
		outlineColor: 'transparent',
		outlineOffset: '0',
		transition: 'outline-color 120ms ease',
		'&:hover': {
			outlineColor: token('color.border'),
		},
	},
	wrapperSelected: {
		outlineColor: token('color.border.focused'),
		'&:hover': {
			outlineColor: token('color.border.focused'),
		},
	},
	wrapperError: {
		outlineColor: token('color.border.danger'),
		'&:hover': {
			outlineColor: token('color.border.danger'),
		},
	},
	label: {
		position: 'absolute',
		top: token('space.negative.150'),
		right: token('space.100'),
		zIndex: 1,
		paddingTop: token('space.025'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.075'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		pointerEvents: 'none',
		userSelect: 'none',
		opacity: 0,
		transition: 'opacity 120ms ease, background-color 120ms ease, color 120ms ease',
	},
	labelVisible: {
		opacity: 1,
	},
	labelSelected: {
		opacity: 1,
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
	},
	labelError: {
		opacity: 1,
		backgroundColor: token('color.background.danger'),
		color: token('color.text.danger'),
		borderColor: token('color.border.danger'),
	},
	body: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
	bodyNoBodyLoading: {
		paddingBlock: 0,
		paddingInline: 0,
	},
	hiddenEditableBody: {
		height: 0,
		overflow: 'hidden',
		visibility: 'hidden',
	},
});

interface Props {
	/** Read-only body content rendered by the manifest renderer. */
	children?: React.ReactNode;
	/** Editable ProseMirror content container used by the editor node view. */
	contentDOM?: HTMLElement;
	/** True when the extension body currently contains generated content. */
	hasGeneratedBodyContent?: boolean;
	/** True when this block is the current PM selection (NodeSelection). */
	isSelected?: boolean;
	/** Runtime lifecycle state for gated execution loading/error behavior. */
	lifecycleState?: AgentManagedBlockLifecycleState;
	/** Retries execution using the saved configuration. */
	onRetry?: () => void;
	/** When true, renders only the body content without any interactive chrome (label, hover border). Used in view-only contexts such as the classic page renderer. */
	readOnly?: boolean;
}

export const shouldShowErrorChrome = ({
	lifecycleState,
}: {
	lifecycleState?: AgentManagedBlockLifecycleState;
}): boolean => lifecycleState === 'execution-error';

/**
 * Custom chrome vs extension default: hover-/selection-revealed Rovo lozenge in the top-right,
 * subtle border on hover, focused border when selected, read-only body.
 */
export const AgentManagedExtensionView = ({
	isSelected,
	children,
	contentDOM,
	hasGeneratedBodyContent,
	lifecycleState,
	onRetry,
	readOnly,
}: Props): JSX.Element => {
	const intl = useIntl();
	const [isInteracted, setIsInteracted] = useState(false);
	const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const handleEnter = useCallback(() => setIsInteracted(true), []);
	const handleLeave = useCallback(() => setIsInteracted(false), []);
	const handleWrapperRef = useCallback((element: HTMLDivElement | null) => {
		setWrapperElement(element);
	}, []);

	/* Intentionally run after every render. React can remount the wrapper while ProseMirror keeps the same contentDOM node alive, so this reattaches the editor-managed body whenever the wrapper changes.
	 */
	useLayoutEffect(() => {
		if (!contentDOM || !bodyRef.current || contentDOM.parentElement === bodyRef.current) {
			return;
		}
		bodyRef.current.appendChild(contentDOM);
	});

	if (readOnly) {
		// In view-only contexts (e.g. classic page renderer), render just the body
		// without any interactive chrome (hover border, label, selection state).
		if (contentDOM) {
			return <Box ref={bodyRef}>{children}</Box>;
		}
		return <React.Fragment>{children}</React.Fragment>;
	}

	const isExecuting = isExecutingLifecycleState(lifecycleState);
	const isNoBodyExecuting = lifecycleState === 'no-body-executing';
	const isExecutionError = lifecycleState === 'execution-error';
	const isNoBodyExecutionError = isExecutionError && !hasGeneratedBodyContent;
	const showErrorChrome = shouldShowErrorChrome({ lifecycleState });
	const showLabel = isInteracted || isSelected || isExecuting || showErrorChrome;
	const showBorderSelected = isSelected && !isExecuting && !showErrorChrome;

	return (
		<div
			css={[
				styles.wrapper,
				showBorderSelected && styles.wrapperSelected,
				showErrorChrome && styles.wrapperError,
			]}
			ref={handleWrapperRef}
			onMouseEnter={handleEnter}
			onMouseLeave={handleLeave}
			onFocus={handleEnter}
			onBlur={handleLeave}
		>
			<AnimatedGradientBorder containerElement={wrapperElement} isExecuting={isExecuting} />
			<span
				css={[
					styles.label,
					showLabel && styles.labelVisible,
					(isSelected || isExecuting) && styles.labelSelected,
					showErrorChrome && styles.labelError,
				]}
			>
				<Inline as="span" space="space.050" alignBlock="center">
					<RovoIcon label="" size="small" />
					{intl.formatMessage(messages.blockLabel)}
				</Inline>
			</span>
			{isNoBodyExecuting ? (
				<div css={[styles.body, styles.bodyNoBodyLoading]}>
					<NoBodyExecutionLoading label={intl.formatMessage(messages.loadingBlockContents)} />
					<div css={styles.hiddenEditableBody} ref={bodyRef}>
						{children}
					</div>
				</div>
			) : isNoBodyExecutionError ? (
				<NoBodyExecutionError contentRef={bodyRef} onRetry={onRetry}>
					{children}
				</NoBodyExecutionError>
			) : (
				<div css={styles.body} ref={bodyRef}>
					{children}
				</div>
			)}
		</div>
	);
};
